import languages from '@/assets/languages.json';
import SettingAutocomplete from '@/components/settings/SettingAutocomplete.tsx';
import SettingChipsField from '@/components/settings/SettingChipsField.tsx';
import { SettingsTextField } from '@/components/settings/SettingsTextField.tsx';
import { dynamicPageGoalsMap } from '@/constants/dynamic-page.ts';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { useSnackbarContext } from '@/contexts/SnackbarContext';
import { UserContext } from '@/contexts/UserContext.tsx';
import { deleteDynamicPage } from '@/services/DynamicPagesService.ts';
import { track } from '@/utils/analytics.ts';
import * as Sentry from '@sentry/react';
import { useEffectDebugger } from '@superfunnel/react-hooks';
import { DynamicPageTag } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { diff } from 'deep-diff';
import { Trash } from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPageSettingsContext } from './DynamicPageSettingsContext';
import { DynamicPageSettingsModalTabWrapper } from './DynamicPageSettingsModalTabWrapper';

export const General = () => {
  const navigate = useNavigate();
  const { show } = useSnackbarContext();

  const [edit, setEdit] = useState(false);
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages, domains } = useContext(DashboardContext);
  const {
    dynamicPageId,
    dynamicPage,
    updatePage,
    updateMetadata,
    hideGeneralFields,
    deleteConfig,
  } = useDynamicPageSettingsContext();

  // Check which fields to hide
  const hideGoal = hideGeneralFields?.includes('goal');
  const hideLogoLink = hideGeneralFields?.includes('logoLink');

  const [name, setName] = useState('');
  const [path, setPath] = useState('');

  const [alternatePathes, setAlternatePathes] = useState<string[]>([]);
  const [websiteLink, setWebsiteLink] = useState<string>('');
  const [language, setLanguage] = useState<(typeof languages)[0]>();
  const [tags, setTags] = useState<DynamicPageTag[]>();
  const [selectedDomains, setSelectedDomains] = useState<string[]>();
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);

  const tagOptions = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    dynamicPages?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values()).map((t) => ({
      id: t.id,
      title: t.name,
    }));
  }, [dynamicPages]);

  const onDiscard = useCallback(() => {
    if (!dynamicPage) return;

    setName(dynamicPage?.name || '');
    setPath(dynamicPage?.path || '');
    setAlternatePathes(dynamicPage?.alternatePathes || []);
    setWebsiteLink(dynamicPage?.config?.websiteLink || '');
    setLanguage(dynamicPage?.language);
    setTags(dynamicPage?.tags);
    setSelectedDomains(dynamicPage?.domains || ['*']);
  }, [dynamicPage]);

  const onSave = useCallback(async () => {
    if (!selectedAccount || !dynamicPageId || !dynamicPage) return;

    const currentData = dynamicPage;

    // Validate domains
    if (!selectedDomains?.length) {
      return;
    }

    const normalizedDomains = selectedDomains.some((d) => d === '*')
      ? null
      : selectedDomains;

    // Build the update payload for updateDynamicPage
    const dynamicPageUpdates: Record<string, any> = {};

    if (currentData.name !== name) {
      dynamicPageUpdates.name = name;
    }

    if (currentData.language?.code !== language?.code) {
      dynamicPageUpdates.language = language;
    }

    if (currentData.config?.websiteLink !== websiteLink) {
      dynamicPageUpdates.config = {
        ...currentData.config,
        websiteLink,
      };
    }

    if (JSON.stringify(currentData.tags) !== JSON.stringify(tags)) {
      dynamicPageUpdates.tags = tags;
    }

    if (
      JSON.stringify(currentData.domains || null) !==
      JSON.stringify(normalizedDomains)
    ) {
      dynamicPageUpdates.domains = normalizedDomains;
    }

    // Build the update payload for updateDynamicPageMetadata
    const metadataUpdates: { path?: string; alternatePathes?: string[] } = {};

    if (currentData.path !== path) {
      metadataUpdates.path = path;
    }

    if (diff(currentData.alternatePathes, alternatePathes)?.length) {
      metadataUpdates.alternatePathes = alternatePathes;
    }

    try {
      let updated = false;

      // Update metadata if there are changes
      if (Object.keys(metadataUpdates).length > 0) {
        await updateMetadata(metadataUpdates);
        updated = true;
      }

      // Update dynamic page data if there are changes
      if (Object.keys(dynamicPageUpdates).length > 0) {
        await updatePage(dynamicPageUpdates);
        updated = true;
      }

      if (updated) {
        show('Updated');
      }
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
    }
  }, [
    selectedAccount,
    dynamicPageId,
    dynamicPage,
    selectedDomains,
    name,
    language,
    websiteLink,
    tags,
    path,
    alternatePathes,
    updatePage,
    updateMetadata,
    show,
  ]);

  useEffect(() => {
    const _selectedDomains = selectedDomains?.some((d) => d === '*')
      ? null
      : selectedDomains;
    setEdit(
      dynamicPage?.name !== name ||
        dynamicPage?.path !== path ||
        JSON.stringify(dynamicPage?.alternatePathes) !==
          JSON.stringify(alternatePathes) ||
        dynamicPage?.config?.websiteLink !== websiteLink ||
        dynamicPage?.language?.code !== language?.code ||
        JSON.stringify(dynamicPage?.tags) !== JSON.stringify(tags) ||
        JSON.stringify(dynamicPage?.domains || null) !==
          JSON.stringify(_selectedDomains || null),
    );
  }, [
    dynamicPage,
    name,
    path,
    alternatePathes,
    websiteLink,
    language,
    tags,
    selectedDomains,
  ]);

  const onDeleteDynamicPageConfirm = useCallback(async () => {
    if (!selectedAccount || !dynamicPageId) return;

    track('delete_dynamic_page_modal_approval_button_click', {
      id: dynamicPageId,
    });

    // Use custom delete handler if provided
    if (deleteConfig?.onDelete) {
      await deleteConfig.onDelete();
      setDeleteConfirmationModalOpen(false);
      return;
    }

    // Default behavior
    deleteDynamicPage(selectedAccount.id, dynamicPageId);
    setDeleteConfirmationModalOpen(false);
    navigate('../../..');
  }, [navigate, selectedAccount, dynamicPageId, deleteConfig]);

  useEffectDebugger(
    () => {
      if (!dynamicPage) return;

      onDiscard();
    },
    [domains, onDiscard, dynamicPage],
    ['domains', 'onDiscard', 'dynamicPage'],
  );

  return (
    <DynamicPageSettingsModalTabWrapper
      edit={edit}
      onDiscard={onDiscard}
      onSave={onSave}
    >
      <AlertModal
        open={deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(false)}
        title={
          deleteConfig?.confirmTitle ??
          // eslint-disable-next-line quotes
          "Are you sure you'd like to delete this Dynamic Page?"
        }
        description="This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setDeleteConfirmationModalOpen(false),
            type: 'secondary',
          },
          {
            title: 'Delete',
            onClick: onDeleteDynamicPageConfirm,
            color: 'negative',
            type: 'primary',
          },
        ]}
        type={'warn'}
      />

      <SettingsTextField title="Name" value={name} setValue={setName} />
      <SettingsTextField
        title="Path"
        value={path}
        setValue={setPath}
        placeholder="Leave empty for root path (/)"
        helpText="The URL path for this page. Leave empty to serve at the root domain."
      />
      <SettingChipsField
        className={'md:col-span-2'}
        title="Alternate Pathes"
        selected={alternatePathes?.map((p) => ({ id: p, title: p })) ?? []}
        setSelected={(selected) => {
          setAlternatePathes(selected.map((t) => t.title));
        }}
        options={[]}
        filterQuery={(t) =>
          t
            .toLocaleLowerCase()
            .trim()
            .replaceAll(/-+/g, '-')
            .replaceAll(/[^a-z0-9-]/g, '')
        }
      />
      {!hideGoal && (
        <div className="font-inter grid w-full items-center gap-1 md:col-span-2">
          <p className="text-base-700 flex items-center gap-2 text-sm font-medium">
            Goal
          </p>
          <Select
            value={
              dynamicPage?.goal ? dynamicPageGoalsMap[dynamicPage.goal] : ''
            }
            disabled={true}
          >
            <SelectTrigger
              className={'rounded-2lg h-10 w-full text-base opacity-100!'}
            >
              <SelectValue placeholder="Select a goal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={
                  (dynamicPage?.goal
                    ? dynamicPageGoalsMap[dynamicPage.goal]
                    : 'unset') || 'unset'
                }
              >
                {dynamicPage?.goal ? dynamicPageGoalsMap[dynamicPage.goal] : ''}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!hideLogoLink && (
        <SettingsTextField
          className={'md:col-span-2'}
          title="Logo link"
          value={websiteLink}
          setValue={setWebsiteLink}
        />
      )}

      <SettingChipsField
        className={'md:col-span-2'}
        title="Tags"
        selected={tags?.map((t) => ({ id: t.id, title: t.name })) ?? []}
        setSelected={(selected) => {
          setTags(selected.map((t) => ({ id: t.id, name: t.title })));
        }}
        options={tagOptions}
      />
      <SettingAutocomplete
        className={'md:col-span-2'}
        title="Language"
        selected={
          language ?? languages.find((l) => l.code === 'en-US') ?? languages[0]
        }
        getValue={(o) => o.name}
        getOptionId={(o) => o.code}
        setSelected={(selected) => {
          setLanguage(selected || undefined);
        }}
        options={languages}
      />

      <SettingChipsField
        className={'md:col-span-2'}
        title="Domains"
        selected={
          selectedDomains?.map((t) => ({
            id: t,
            title: t === '*' ? 'All Domains' : t,
          })) ?? []
        }
        setSelected={(selected) => {
          if (selected[selected.length - 1]?.id === '*') {
            setSelectedDomains(['*']);
            return;
          } else {
            setSelectedDomains(
              selected.map((t) => t.id).filter((d) => d !== '*'),
            );
          }
        }}
        options={[
          { id: '*', title: 'All Domains' },
          ...(domains?.map((d) => ({
            id: d.data().domain,
            title: d.data().domain,
          })) ?? []),
        ]}
      />
      <Button
        variant={'outline'}
        size={'sm'}
        className={'text-error-600'}
        onClick={() => {
          track('delete_dynamic_page_button_click', {
            id: dynamicPageId,
          });
          setDeleteConfirmationModalOpen(true);
        }}
      >
        <Trash className={'size-4.5'} />
        {deleteConfig?.buttonText ?? 'Delete Dynamic Page'}
      </Button>
    </DynamicPageSettingsModalTabWrapper>
  );
};
